/**
 * Branch handoff between this app and the automated dispatch app (v2), which
 * run on different origins.
 *
 * Mirrors `src/lib/branchHandoff.ts` in bellissimo-io-automated-dispatch-ui.
 * Ownership follows the per-branch `isAutomationEnabled` flag: true belongs to
 * v2, false belongs here. The branch id travels in the URL, since localStorage
 * is scoped per origin.
 */
import {LOCAL_STORAGE_MAIN_BRANCH_ID} from "./constants/LocalStorage";

const BRANCH_PARAM = "branch_id"
const FROM_PARAM = "from"

/** Stamped on outgoing links; v2 stamps its own value back. */
const THIS_APP = "legacy"
const V2_APP = "v2"

/** Origin of the current tab, used by the loop guard. */
const ORIGIN_KEY = "_DISPATCH_HANDOFF_ORIGIN_"

/**
 * Loose shape of an iiko id: keeps junk out of localStorage and request paths.
 * Authorization is still enforced by ApplicationDataProvider, which falls back
 * to an owned branch when the stored id is not in the user's list.
 */
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,63}$/

interface Arrival {
    from: string
    branchId: string
}

const readArrival = (): Arrival | null => {
    try {
        const raw = sessionStorage.getItem(ORIGIN_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== "object") return null
        const {from, branchId} = parsed as Partial<Arrival>
        if (typeof from !== "string" || typeof branchId !== "string") return null
        return {from, branchId}
    } catch {
        // Private mode, disabled storage or a hand-edited value: skip the guard.
        return null
    }
}

const rememberArrival = (arrival: Arrival) => {
    try {
        sessionStorage.setItem(ORIGIN_KEY, JSON.stringify(arrival))
    } catch {
        /* see readArrival */
    }
}

/**
 * Stores the branch id handed over by v2 as the selected branch and strips both
 * parameters from the address bar so a reload or bookmark does not replay them.
 *
 * Call once, before the first render.
 */
export const readIncomingBranch = () => {
    const url = new URL(window.location.href)
    const branchId = url.searchParams.get(BRANCH_PARAM) ?? ""
    const from = url.searchParams.get(FROM_PARAM) ?? ""

    if (!branchId && !from) return

    url.searchParams.delete(BRANCH_PARAM)
    url.searchParams.delete(FROM_PARAM)
    window.history.replaceState(null, "", url.toString())

    if (!ID_PATTERN.test(branchId)) return

    rememberArrival({from, branchId})
    // Must match the JSON encoding `useLocalStorage` writes.
    localStorage.setItem(LOCAL_STORAGE_MAIN_BRANCH_ID, JSON.stringify(branchId))
}

/**
 * Rollout gate for the outbound handoff, off unless the value is exactly "true",
 * so deploying the code changes nothing until it is enabled per region.
 *
 * Intentionally asymmetric with v2, which has no such switch: a non-automated
 * branch is unusable there, while an automated branch still works here.
 */
export const isHandoffToV2Enabled = (): boolean =>
    process.env.REACT_APP_HANDOFF_TO_V2_ENABLED === "true"

/**
 * True when v2 sent us here for this branch, meaning a redirect back would
 * ping-pong the tab on two disagreeing snapshots of the flag. Scoped to the
 * arriving branch: switching to another automated branch is a real handoff.
 */
export const arrivedFromV2 = (branchId: string): boolean => {
    const arrival = readArrival()
    return arrival?.from === V2_APP && arrival.branchId === branchId
}

/** Clears the v2 arrival marker so a later automation switch-on can hand off again. */
export const forgetArrival = () => {
    try {
        sessionStorage.removeItem(ORIGIN_KEY)
    } catch {
        /* see readArrival */
    }
}

/**
 * v2 URL carrying `branchId`, or null when the handoff is not configured. The
 * origin comes from the build-time env only: deriving it from a socket payload
 * would expose an open redirect.
 */
export const dispatchV2UrlFor = (branchId: string): string | null => {
    const base = process.env.REACT_APP_DISPATCH_V2_URL
    if (!base || !branchId) return null

    let url: URL
    try {
        url = new URL(base)
    } catch {
        return null
    }
    if (url.protocol !== "https:" && url.protocol !== "http:") return null

    url.searchParams.set(BRANCH_PARAM, branchId)
    url.searchParams.set(FROM_PARAM, THIS_APP)
    return url.toString()
}

/**
 * Hands the tab over to v2. Uses `replace` so Back does not return to the app
 * that just released the branch. Returns false when the handoff is
 * unconfigured, leaving the caller on the current board.
 */
export const redirectToDispatchV2 = (branchId: string): boolean => {
    const url = dispatchV2UrlFor(branchId)
    if (!url) return false

    window.location.replace(url)
    return true
}
